/**
 * Conversation Buffer Utility
 * 
 * This utility maintains a buffer of recent messages for each conversation
 * and provides functions to analyze combined message content for prohibited content.
 * This helps detect attempts to bypass filters by splitting prohibited content across multiple messages.
 */

import { detectFilteredContent } from './messageFilter';

// In-memory store for conversation buffers
// In a production environment, this should be moved to a database or Redis
const conversationBuffers = {};

// Constants for buffer configuration
const MAX_BUFFER_SIZE = 10; // Maximum number of messages to store in buffer
const BUFFER_TIME_WINDOW = 10 * 60 * 1000; // 10 minutes in milliseconds
const MIN_MESSAGES_TO_CHECK = 2; // Minimum number of messages before performing combined check

/**
 * Add a message to the conversation buffer
 * @param {string} conversationId - The conversation ID
 * @param {string} senderId - The sender's user ID
 * @param {string} messageText - The message text
 * @returns {object} - Result of content check on combined messages
 */
export const addMessageToBuffer = (conversationId, senderId, messageText) => {
  const now = Date.now();
  
  // Initialize buffer if it doesn't exist
  if (!conversationBuffers[conversationId]) {
    conversationBuffers[conversationId] = {
      messages: [],
      userBuffers: {}
    };
  }
  
  const buffer = conversationBuffers[conversationId];
  
  // Initialize user buffer if it doesn't exist
  if (!buffer.userBuffers[senderId]) {
    buffer.userBuffers[senderId] = [];
  }
  
  const userBuffer = buffer.userBuffers[senderId];
  
  // Add current message to user's buffer
  userBuffer.push({
    text: messageText,
    timestamp: now
  });
  
  // Remove messages older than the time window
  const updatedUserBuffer = userBuffer.filter(msg => msg.timestamp > now - BUFFER_TIME_WINDOW);
  buffer.userBuffers[senderId] = updatedUserBuffer;
  
  // If we don't have enough messages yet, no need to check
  if (updatedUserBuffer.length < MIN_MESSAGES_TO_CHECK) {
    return { isClean: true, detections: [] };
  }
  
  // Check combinations of messages
  return checkCombinedMessages(updatedUserBuffer);
};

/**
 * Check combinations of messages for prohibited content
 * @param {Array} messageBuffer - Array of message objects with text and timestamp
 * @returns {object} - Result of content check
 */
const checkCombinedMessages = (messageBuffer) => {
  // Ensure buffer is sorted by timestamp (oldest first)
  const sortedBuffer = [...messageBuffer].sort((a, b) => a.timestamp - b.timestamp);
  
  // Check different combinations of messages
  
  // 1. Check all messages combined (with spaces between)
  const allCombined = sortedBuffer.map(msg => msg.text).join(' ');
  const allCombinedCheck = detectFilteredContent(allCombined);
  if (!allCombinedCheck.isClean) {
    return allCombinedCheck;
  }
  
  // 2. Check all messages combined (without spaces)
  const allCombinedNoSpaces = sortedBuffer.map(msg => msg.text).join('');
  const allCombinedNoSpacesCheck = detectFilteredContent(allCombinedNoSpaces);
  if (!allCombinedNoSpacesCheck.isClean) {
    return allCombinedNoSpacesCheck;
  }
  
  // 3. Check sequential pairs of messages
  for (let i = 0; i < sortedBuffer.length - 1; i++) {
    // With space
    const pairWithSpace = `${sortedBuffer[i].text} ${sortedBuffer[i + 1].text}`;
    const pairWithSpaceCheck = detectFilteredContent(pairWithSpace);
    if (!pairWithSpaceCheck.isClean) {
      return pairWithSpaceCheck;
    }
    
    // Without space
    const pairNoSpace = `${sortedBuffer[i].text}${sortedBuffer[i + 1].text}`;
    const pairNoSpaceCheck = detectFilteredContent(pairNoSpace);
    if (!pairNoSpaceCheck.isClean) {
      return pairNoSpaceCheck;
    }
  }
  
  // 4. Check for sequences of digits that might form a phone number
  // Extract all digits from messages
  const allDigits = sortedBuffer.map(msg => msg.text.replace(/\D/g, '')).join('');
  if (allDigits.length >= 7) { // Most phone numbers have at least 7 digits
    const digitOnlyCheck = detectFilteredContent(allDigits);
    if (!digitOnlyCheck.isClean) {
      return digitOnlyCheck;
    }
  }
  
  // 5. Check for email components (username@domain)
  // This is a simplified check - in production you might want more sophisticated detection
  const allText = sortedBuffer.map(msg => msg.text).join('');
  if (allText.includes('@')) {
    const emailCheck = detectFilteredContent(allText);
    if (!emailCheck.isClean) {
      return emailCheck;
    }
  }
  
  // No prohibited content detected in any combination
  return { isClean: true, detections: [] };
};

/**
 * Clear the buffer for a specific conversation
 * @param {string} conversationId - The conversation ID to clear
 */
export const clearConversationBuffer = (conversationId) => {
  if (conversationBuffers[conversationId]) {
    delete conversationBuffers[conversationId];
  }
};

/**
 * Get the current buffer for a conversation (for debugging)
 * @param {string} conversationId - The conversation ID
 * @returns {object} - The conversation buffer
 */
export const getConversationBuffer = (conversationId) => {
  return conversationBuffers[conversationId] || { messages: [], userBuffers: {} };
};
